import { useToast } from '@/hooks/use-toast';
import { Toast, ToastClose, ToastDescription, ToastProvider, ToastTitle, ToastViewport } from '@/components/ui/toast';

type ToasterPosition = 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right';

interface ToasterProps {
  position?: ToasterPosition;
  filterVariant?: 'destructive' | 'non-destructive';
}

function getViewportPositionClasses(position: ToasterPosition): string {
  // Mobile: always top (handled in ToastViewport base classes)
  // Desktop (md+): use configured position
  switch (position) {
    case 'top-left':
      return 'md:top-0 md:left-0 md:right-auto md:bottom-auto';
    case 'top-center':
      return 'md:top-0 md:left-1/2 md:-translate-x-1/2 md:right-auto md:bottom-auto';
    case 'top-right':
      return 'md:top-0 md:right-0 md:left-auto md:bottom-auto';
    case 'bottom-left':
      return 'md:bottom-20 md:left-0 md:right-auto'; // bottom-20 to clear potential footer
    case 'bottom-center':
      return 'md:bottom-20 md:left-1/2 md:-translate-x-1/2 md:right-auto';
    case 'bottom-right':
    default:
      return 'md:bottom-20 md:right-0 md:left-auto'; // bottom-20 to clear potential footer
  }
}

export function Toaster({ position = 'bottom-right', filterVariant }: ToasterProps): React.JSX.Element {
  const { toasts } = useToast();

  return (
    <ToastProvider>
      {toasts
        .filter((t) => {
          if (!filterVariant) return true;
          const isDestructive = t.variant === 'destructive';
          return filterVariant === 'destructive' ? isDestructive : !isDestructive;
        })
        .map(function ({ id, title, description, action, ...props }) {
          return (
            <Toast key={id} {...props}>
              <div className="grid gap-token-xs">
                {title && <ToastTitle>{title}</ToastTitle>}
                {description && <ToastDescription>{description}</ToastDescription>}
              </div>
              {action}
              <ToastClose />
            </Toast>
          );
        })}
      <ToastViewport className={getViewportPositionClasses(position)} />
    </ToastProvider>
  );
}
